import { useInfiniteScroll } from '@consigli/hooks';
import { type UIEvent, useCallback, useEffect, useRef, useState } from 'react';

/**
 * How far through the loaded rows the user scrolls before the next chunk is
 * asked for, so it has usually arrived by the time they reach the end.
 */
const PREFETCH_SCROLL_FRACTION = 0.5;

export interface AddContextPage<T> {
  next?: string | null;
  results: T[];
}

interface UseAddContextListOptions<T> {
  /** Serialised filters. When it changes the list resets to page one. */
  filterKey: string;
  /**
   * `signal` aborts when the filters change. Tie it to the request so a search
   * the user has typed past stops loading.
   */
  fetchPage: (page: number, signal: AbortSignal) => Promise<AddContextPage<T>>;
  /** Paused while the dialog is closed, so opening it fetches fresh rows. */
  enabled: boolean;
}

export interface UseAddContextListResult<T> {
  error: unknown;
  hasNextPage: boolean;
  /** True on the first page, and again whenever the filters change. */
  isLoading: boolean;
  /** True while a later page is being appended. */
  isFetchingMore: boolean;
  items: T[];
  /** Attach to the scroll container. Asks for the next chunk halfway down the loaded rows. */
  onScroll: (event: UIEvent<HTMLElement>) => void;
  /**
   * Attach to a sentinel at the end of the list. It covers a list too short to
   * scroll, which never fires `onScroll`.
   */
  triggerRef: ReturnType<typeof useInfiniteScroll>;
}

/**
 * Pages a list in on scroll instead of through a pager, which is what the modal
 * needs and what the Findings and Documents pages do not do. The shape follows
 * `use-project-list.ts`, the one place in the app that already does this: a lazy
 * query, an accumulating array, and a generation counter so a page that resolves
 * after the filters moved is dropped rather than appended to the wrong list.
 * A filter change also aborts the requests the old filters still have in
 * flight, so the server stops working on a search nobody is waiting for.
 *
 * Generic over the row type because findings and documents differ only in which
 * query they call.
 */
export const useAddContextList = <T>({
  enabled,
  fetchPage,
  filterKey,
}: UseAddContextListOptions<T>): UseAddContextListResult<T> => {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const pageRef = useRef(1);
  const generationRef = useRef(0);
  // Set before the state that mirrors it renders: scroll events arrive faster
  // than re-renders, and a second one reading stale state would skip a page.
  const isFetchingMoreRef = useRef(false);
  // One per filter key, shared by its pages, so a new key aborts all of them.
  const abortRef = useRef<{ controller: AbortController; filterKey: string } | null>(null);

  const load = useCallback(
    async (page: number) => {
      const generation = generationRef.current;
      const signal = abortRef.current?.controller.signal ?? new AbortController().signal;

      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const result = await fetchPage(page, signal);

        // The filters moved while this was in flight; these rows are not ours.
        if (generation !== generationRef.current) return;

        setError(null);
        setHasNextPage(!!result.next);
        setItems((previous) => (page === 1 ? result.results : [...previous, ...result.results]));
      } catch (caught) {
        if (generation !== generationRef.current || signal.aborted) return;
        setError(caught);
        setHasNextPage(false);
      } finally {
        // Only this request's own flags: clearing them from a stale response
        // would hide a spinner belonging to one still in flight.
        if (generation === generationRef.current) {
          isFetchingMoreRef.current = false;
          setIsLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [fetchPage],
  );

  // One effect owns the reset and the first fetch. Splitting them let a stale
  // `hasNextPage` survive a filter change, so the next scroll tick asked for
  // page two of a list that had only just been replaced.
  useEffect(() => {
    if (!enabled) return;

    /*
     * Aborted here, on a new key, and never in a cleanup. StrictMode runs this
     * effect twice with the same arguments, and RTK hands the second call the
     * first one's in-flight request: aborting that request would fail both.
     */
    if (abortRef.current?.filterKey !== filterKey) {
      abortRef.current?.controller.abort();
      abortRef.current = { controller: new AbortController(), filterKey };
    }
    generationRef.current += 1;
    isFetchingMoreRef.current = false;
    pageRef.current = 1;
    setItems([]);
    setHasNextPage(false);
    void load(1);
  }, [enabled, filterKey, load]);

  const fetchNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingMoreRef.current || isLoading) return;

    isFetchingMoreRef.current = true;
    pageRef.current += 1;
    void load(pageRef.current);
  }, [hasNextPage, isLoading, load]);

  const triggerRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage,
    rootMargin: '200px',
  });

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      if (scrollTop + clientHeight >= scrollHeight * PREFETCH_SCROLL_FRACTION) fetchNextPage();
    },
    [fetchNextPage],
  );

  return { error, hasNextPage, isLoading, isFetchingMore, items, onScroll, triggerRef };
};
